// Skip links, the route announcer and the screen error boundary (shell-ia §4.4; DESIGN-SYSTEM §15). WP-SHELL.
// - Skip links are the first focusables: "Skip to content" (#main) and "Skip to the agent" (the sidebar composer).
// - On every navigation: document.title = "{title} · ECONIC" (Arabic «… · إيكونيك»), a polite live region announces it, and focus moves to
//   the screen's h1 unless the change came from inside the content (a tab, a link in the page) — then the person's place is kept.
// - A screen that throws shows "This screen could not load · Reference …" inside the frame; the chrome keeps working. The same card is the
//   router's error element: on each signed-in route (it renders in the shell's content column, inside every shell provider) and, without
//   any shell hook, at the root for an error above the frame (RootErrorElement).
import { Component, useEffect, useMemo, useRef, useState, type ErrorInfo, type MouseEvent, type ReactNode } from "react";
import { RotateCcw } from "lucide-react";
import { Link, useLocation, useRouteError } from "react-router";
import { useLocale, useTranslation } from "@ncp/i18n";
import { useCurrentChrome } from "../../shell/chrome";
import { routeFor } from "../../app/routes";

export function SkipLinks() {
  const { t } = useTranslation();
  const toAgent = (e: MouseEvent) => {
    const target = document.querySelector<HTMLElement>("aside[data-dock-panel] textarea, aside[data-dock-panel] [data-agent-open], [data-ask-open]");
    if (target) {
      e.preventDefault();
      target.focus();
      if (target.matches("[data-agent-open], [data-ask-open]")) target.click();
    }
  };
  return (
    <>
      <a href="#main" className="es-skip" onClick={(e) => { e.preventDefault(); document.getElementById("main")?.focus(); }}>{t("assistant:econic.shell.skip.content")}</a>
      <a href="#agent" className="es-skip" onClick={toAgent}>{t("assistant:econic.shell.skip.agent")}</a>
    </>
  );
}

export function RouteAnnouncer() {
  const { t } = useTranslation();
  const { locale } = useLocale();
  const { pathname } = useLocation();
  const chrome = useCurrentChrome();
  const [message, setMessage] = useState("");
  const first = useRef(true);
  const def = routeFor(pathname);
  const brand = locale === "ar" ? "إيكونيك" : "ECONIC";
  const title = chrome?.title || (def ? t(`assistant:${def.title}`) : "");

  useEffect(() => {
    // Desks name themselves (CustomPage sets document.title from the page); everything else is named here.
    if (pathname.startsWith("/p/")) return;
    // ECONIC's own screens (the landing, a conversation) are titled by the brand alone, never "ECONIC · ECONIC".
    document.title = title && title !== brand ? `${title} · ${brand}` : brand;
  }, [title, brand, pathname]);

  useEffect(() => {
    if (first.current) {
      first.current = false;
      return;
    }
    const main = document.getElementById("main");
    const from = document.activeElement;
    const inside = !!main && !!from && from !== document.body && main.contains(from);
    const timer = window.setTimeout(() => {
      const label = document.title;
      setMessage(label);
      if (inside) return;
      const h1 = main?.querySelector<HTMLElement>("h1");
      if (h1) {
        if (!h1.hasAttribute("tabindex")) h1.setAttribute("tabindex", "-1");
        h1.focus({ preventScroll: true });
      }
    }, 120);
    return () => window.clearTimeout(timer);
  }, [pathname]);

  return <div className="sr-only" role="status" aria-live="polite" aria-atomic="true">{message}</div>;
}

interface BoundaryState { error: Error | null; ref: string | null }

class Boundary extends Component<{ children: ReactNode; resetKey: string; render: (ref: string, retry: () => void) => ReactNode }, BoundaryState> {
  state: BoundaryState = { error: null, ref: null };
  static getDerivedStateFromError(error: Error): BoundaryState {
    return { error, ref: `UI-${Date.now().toString(36).toUpperCase()}` };
  }
  componentDidCatch(error: Error, info: ErrorInfo) {
    console.error("[econic] screen error", this.state.ref, error, info.componentStack);
  }
  componentDidUpdate(prev: { resetKey: string }) {
    if (prev.resetKey !== this.props.resetKey && this.state.error) this.setState({ error: null, ref: null });
  }
  render() {
    if (this.state.error) return this.props.render(this.state.ref ?? "", () => this.setState({ error: null, ref: null }));
    return this.props.children;
  }
}

/** "This screen could not load": the reference, Go to Today (secondary) and Try again (primary, last). Uses no shell hook. */
function ScreenErrorCard({ reference, onRetry, home = true }: { reference: string; onRetry: () => void; home?: boolean }) {
  const { t } = useTranslation();
  return (
    <section role="alert" className="mx-auto mt-6 w-full max-w-xl">
      <div className="ec-card">
        <div className="ec-card-b flex flex-col gap-2 p-4">
          <h1 className="t-title2" tabIndex={-1}>{t("assistant:econic.shell.error.screen")}</h1>
          <p className="t-small">{t("assistant:econic.shell.error.screenBody")}</p>
          <p className="t-callout">{t("assistant:econic.shell.error.reference")} <bdi dir="ltr" className="font-mono">{reference}</bdi></p>
          <div className="mt-1 flex flex-wrap justify-end gap-2">
            {home ? <Link to="/" className="ec-btn secondary sm">{t("assistant:econic.shell.error.goToday")}</Link> : null}
            <button type="button" className="ec-btn primary sm" onClick={onRetry}><RotateCcw className="size-3.5" aria-hidden />{t("assistant:econic.shell.error.retry")}</button>
          </div>
        </div>
      </div>
    </section>
  );
}

export function ShellErrorBoundary({ children }: { children: ReactNode }) {
  const { pathname } = useLocation();
  return (
    <Boundary resetKey={pathname} render={(ref, retry) => <ScreenErrorCard reference={ref} onRetry={retry} />}>
      {children}
    </Boundary>
  );
}

function useErrorReference(): string {
  const error = useRouteError();
  const ref = useMemo(() => `UI-${Date.now().toString(36).toUpperCase()}`, []);
  useEffect(() => {
    console.error("[econic] route error", ref, error);
  }, [ref, error]);
  return ref;
}

/** The router's error element of a signed-in route: it renders in the shell's content column (inside every shell provider). */
export function RouteErrorElement() {
  const ref = useErrorReference();
  return <ScreenErrorCard reference={ref} onRetry={() => window.location.reload()} />;
}

/** The router's error element above the frame (no shell provider is mounted there): the same card on the grouped window. */
export function RootErrorElement() {
  const ref = useErrorReference();
  return (
    <main className="min-h-dvh bg-[var(--bg-grouped,#F5F5F7)] px-4 py-10" data-econic-error="">
      <ScreenErrorCard reference={ref} onRetry={() => window.location.reload()} home={false} />
    </main>
  );
}
